import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { ErrorReverseIcon } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import Preloader from "~/components/Preloader";
import useAcceptCamera from "~/entity/Ivideon/hooks/useAcceptCamera";
import useGetDealerGroups from "~/pages/Ivideon/hooks/useGetDealerGroups";
import CameraViewModal from "~/pages/Ivideon/modals/CameraViewModal";
import Wrapper from "~/shared/ui/Wrapper";
import {
  IIvideonGetterTokenSchema,
  useCameraCheckStatus,
  useIvideon,
} from "~/entity/Ivideon";
import { useURLParamsGetter } from "~/hooks/useURLParamsSetter";
import {
  cameraHasTariff,
  ERROR_PREVIEW_IMAGE,
} from "~/pages/Ivideon/assets/options";
import { getIvideonCameraColumns } from "~/pages/Ivideon/model/tableAdapter";
import { IvideonHTTP } from "~/services/ivideon/ivideonHTTP";
import { getConfiguredAxiosInstance } from "~/services/ivideon/lib/ConfigureAxiosInstance";
import { usePermissions } from "~/shared/context/PermissionContext";
import { TableV2 } from "~/shared/ui/TableV2";

import CameraTitleButtonGroup from "./ui/CameraTitleButtonGroup";
import RejectedModal from "./ui/RejectedModal";
import { useIvideonDealerById } from "..";

import s from "./s.less";

const CameraList = () => {
  const [resetKey, setResetKey] = useState(0);
  const history = useHistory();
  const { t } = useTranslation();
  const { hasPermission, loading: loadingPermissions } = usePermissions();

  const { cameraId, currentDealerId } = useParams<{
    cameraId: string;
    currentDealerId: string;
  }>();
  const [openModal, setOpenModal] = useState(false);
  const [ivideonIsAuth, setIvideonIsAuth] = useState(null);
  const [currentCameraId, setCurrentCameraId] = useState(null);
  const [items, setItems] = useState([]);
  const toggleIvideonIsAuth = (bool: boolean) => setIvideonIsAuth(bool);

  const currentDealerInfoFromCookie = useIvideonDealerById(currentDealerId);
  const [image, setImage] = useState(null);
  const [cameraListCurrentGroup, setCameraListCurrentGroup] = useState([]);
  const [cameraGroupListName, setCameraGroupListName] = useState();
  const { openModalReject } = useURLParamsGetter<{
    openModalReject: "rejected";
  }>();
  const { data: cameraStatus, getDealerId } = useCameraCheckStatus();
  const { acceptCamera } = useAcceptCamera();
  const { ivideonGetToken } = useIvideon();
  const [getTokenInfo, setGetTokenInfo] = useState<IIvideonGetterTokenSchema>();

  useEffect(() => {
    if (!currentDealerId) return;
    ivideonGetToken(currentDealerId)
      .then((res) => setGetTokenInfo(res.data.token))
      .catch(() => history.push("/ivideon/dealers/"));
  }, [currentDealerId]);

  const nextKey = () => setResetKey((prev) => prev + 1);

  const [showImages, setShowImages] = useState(() => {
    const saved = localStorage.getItem("showImages");
    return saved ? JSON.parse(saved) : false;
  });

  const {
    data: dataGroupsCameras,
    isLoading,
    refetch,
  } = useGetDealerGroups(currentDealerId, ivideonIsAuth, cameraStatus?.status);

  const handleAcceptCamera = async (cameraId: string, message?: string) => {
    if (message) {
      await acceptCamera({
        id: Number(cameraId),
        dealerId: Number(currentDealerId),
        accept: false,
        reason: message,
      });
    } else {
      await acceptCamera({
        id: Number(cameraId),
        dealerId: Number(currentDealerId),
        accept: true,
      });
    }
    refetch();
    sessionStorage.removeItem("cameraId");
  };

  useEffect(() => {
    if (currentDealerId) {
      getDealerId(currentDealerId);
    }
  }, [currentDealerId]);

  useEffect(() => {
    if (!isLoading && dataGroupsCameras?.length) {
      const foundCameraGroup = dataGroupsCameras.filter(
        (item) => item.id === cameraId,
      )[0];
      setItems(() =>
        foundCameraGroup.cameras
          .map((item: any) => ({
            id: item.id,
            ivideon_id: item.ivideon_id,
            name: item.name,
            lastStatus: item.lastStatus,
            isAcceptance: item.isAcceptance,
            totalOfflineMinutes: item?.totalOfflineMinutes,
            deacceptReason: item.deacceptReason,
          }))
          .sort((a, b) => a.id - b.id),
      );
      setCameraListCurrentGroup(foundCameraGroup.cameras);
      setCameraGroupListName(foundCameraGroup.name);
    }
  }, [dataGroupsCameras, isLoading, cameraId]);

  useEffect(() => {
    if (getTokenInfo?.access_token) {
      setIvideonIsAuth(getTokenInfo.access_token);
    }
  }, [getTokenInfo]);

  useEffect(() => {
    localStorage.setItem("showImages", JSON.stringify(showImages));
  }, [showImages]);

  const handleOpenModal = (cameraId: string) => {
    setOpenModal(true);
    setCurrentCameraId(cameraId);
  };

  const toggleShowImages = () => {
    setShowImages((prevState) => !prevState);
  };

  const goBack = () => {
    setCameraListCurrentGroup([]);
    history.push(`/ivideon/dealers/${currentDealerId}`);
  };

  const goHistoryCamera = (cameraIvideonId: string, cameraName: string) => {
    const url = `/ivideon/cameras/history/${currentDealerId}?cameraId=${cameraIvideonId}&cameraName=${encodeURIComponent(cameraName)}`;
    window.open(url, "_blank");
  };

  useEffect(() => {
    if (cameraListCurrentGroup?.length && getTokenInfo?.access_token) {
      IvideonHTTP.resetInstance();
      const ivideon = IvideonHTTP.getInstance(
        getConfiguredAxiosInstance({
          baseURL: `https://${getTokenInfo.open_api_host}`,
          access_token: getTokenInfo?.access_token,
          api_host: getTokenInfo?.open_api_host,
          refresh_token: "",
          api4_host: getTokenInfo?.api_host,
          expires_in: Number(getTokenInfo?.expires) || 84600,
          toggleIvideonIsAuth: toggleIvideonIsAuth,
          id: currentDealerId,
        }),
        toggleIvideonIsAuth,
        currentDealerId,
      );

      const fetchImages = async () => {
        const fetchedImages = await Promise.all(
          cameraListCurrentGroup.map(async (item) => {
            try {
              const response = await ivideon.getCameraPreview(item.ivideon_id);
              if (response.data) {
                const base64String = bufferToBase64(response.data);
                return {
                  id: item.ivideon_id,
                  url: `data:image/jpeg;base64,${base64String}`,
                };
              }
            } catch (error) {
              console.error(
                `Error fetching camera preview for cameraId ${item.ivideon_id}:`,
                error,
              );
              return {
                id: item.ivideon_id,
                url: "data:image/svg+xml;base64," + btoa(ERROR_PREVIEW_IMAGE),
              };
            }
          }),
        );

        const validImages = fetchedImages.filter((image) => image !== null);
        setImage(validImages);
      };

      fetchImages();
    }
  }, [cameraListCurrentGroup.length, getTokenInfo]);

  function bufferToBase64(buf) {
    return Buffer.from(buf, "binary").toString("base64");
  }

  const currentGroupName = cameraGroupListName || "";
  const isSaleZone = currentGroupName.toLowerCase().trim() === "зона продаж";
  const isServiceZone =
    currentGroupName.toLowerCase().trim() === "зона сервиса";

  const hasAcceptPermission =
    (isSaleZone && hasPermission("ivideonGroups.acceptGroup.sale")) ||
    (isServiceZone && hasPermission("ivideonGroups.acceptGroup.service")) ||
    (!isSaleZone && !isServiceZone);

  const columns = getIvideonCameraColumns(t, {
    s,
    handleAcceptCamera,
    goHistoryCamera,
    resetKey,
    acceptanceSelectReadOnly: !hasAcceptPermission,
    hasTariffCheck: cameraHasTariff,
  });

  return (
    <Wrapper.Content
      txtBtn="Назад"
      goBackHandler={goBack}
      title={`${t("ivideonCameras.titleCamerasList")} (${
        cameraGroupListName || "..."
      })`}
      controlComponent={
        <CameraTitleButtonGroup
          showImages={showImages}
          toggleShowImages={toggleShowImages}
          currentDealerId={currentDealerId}
        />
      }
    >
      <div className={s.container}>
        <div className={clsx([s.scan])}>
          <ErrorReverseIcon size={20} fill="#FF9E1B" />
          {t("ui.error_name_camera")}
        </div>
        <div className={s.containerPart}>
          {showImages ? (
            <TableV2
              columns={columns}
              dataSource={items ?? []}
              rowKey="id"
              loading={isLoading}
              stripedRows={false}
              noHorizontalScroll
              onRow={(record: any) => ({
                onClick: () => handleOpenModal(record.ivideon_id),
                style: { cursor: "pointer" },
              })}
            />
          ) : (
            <div className={s.prevWrapper}>
              {isLoading ? (
                <Preloader />
              ) : image && image.length ? (
                image.map((img) => (
                  <div className={s.photoWrapper} key={JSON.stringify(img)}>
                    <img
                      className={s.photo}
                      src={img.url}
                      alt={`Camera ${img.id}`}
                      onClick={() => handleOpenModal(img.id)}
                    />
                    <span>
                      {cameraListCurrentGroup.find(
                        (item) => item?.ivideon_id === img.id,
                      )?.name ?? ""}
                    </span>
                  </div>
                ))
              ) : (
                <Preloader />
              )}
            </div>
          )}
        </div>
        {openModal && (
          <CameraViewModal
            openModal={openModal}
            setOpenModal={setOpenModal}
            cameraId={currentCameraId}
            accessToken={getTokenInfo.access_token}
            apiHost={getTokenInfo.open_api_host}
          />
        )}
        {openModalReject && (
          <RejectedModal
            handleAcceptCamera={handleAcceptCamera}
            nextKey={nextKey}
          />
        )}
      </div>
    </Wrapper.Content>
  );
};

export default CameraList;
